'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const FEATURES_BENTO = [
  {
    id: 'live',
    size: 'large',
    icon: 'SignalIcon',
    tag: 'LIVE NHL',
    title: 'Données en temps réel',
    subtitle: '30 secondes de délai maximum',
    desc: 'Blessures, alignements, confirmations gardiens et mouvements de cotes — tout en temps réel avec 99.2% d\'uptime garanti.',
    metric: '99.2%',
    metricLabel: 'Uptime données',
    accent: 'from-positive/20 to-positive/5',
    border: 'border-positive/30',
  },
  {
    id: 'agents',
    size: 'small',
    icon: 'SparklesIcon',
    tag: 'AI AGENTS',
    title: '6 Agents IA',
    subtitle: 'surveillance 24/7',
    desc: 'Agents autonomes qui analysent et alertent.',
    metric: '6',
    metricLabel: 'Agents actifs',
    accent: 'from-purple-500/15 to-purple-500/5',
    border: 'border-purple-500/30',
  },
  {
    id: 'sim',
    size: 'small',
    icon: 'BeakerIcon',
    tag: 'SIMULATION',
    title: 'Monte Carlo',
    subtitle: '50,000 itérations',
    desc: 'Distributions probabilistes complètes. SimulationLab avec scénarios personnalisés.',
    metric: '50k',
    metricLabel: 'Itérations',
    accent: 'from-amber-500/15 to-amber-500/5',
    border: 'border-amber-500/30',
  },
  {
    id: 'terminal',
    size: 'medium',
    icon: 'CommandLineIcon',
    tag: 'QUANT TERMINAL',
    title: 'Bloomberg-Style',
    subtitle: 'pour la NHL',
    desc: 'Raccourcis clavier, mode dense, command bar. Interface institutionnelle.',
    metric: '∞',
    metricLabel: 'Requêtes',
    accent: 'from-cyan-500/15 to-cyan-500/5',
    border: 'border-cyan-500/30',
  },
  {
    id: 'analytics',
    size: 'medium',
    icon: 'ChartBarIcon',
    tag: 'ANALYTICS PRO',
    title: 'Insights institutionnels',
    subtitle: 'niveau professionnel',
    desc: 'xGoals, Corsi, Fenwick, splits avancés. Toutes les métriques des analystes professionnels.',
    metric: '40+',
    metricLabel: 'Métriques',
    accent: 'from-primary/15 to-primary/5',
    border: 'border-primary/30',
  },
];

const SOCIAL_PROOF = [
  { name: 'Marc D.', role: 'Analyste sportif', quote: '+12% ROI sur 3 mois grâce aux projections ML.', avatar: 'MD' },
  { name: 'Sophie L.', role: 'Quant researcher', quote: 'Le terminal Bloomberg-style est exactement ce dont j\'avais besoin.', avatar: 'SL' },
  { name: 'Alex T.', role: 'Fantasy NHL Pro', quote: 'Les alertes gardiens en temps réel ont transformé ma stratégie.', avatar: 'AT' },
];

const INCLUDED = [
  'Projections ML illimitées', 'Live Analytics NHL', 'ML Engine complet',
  'Alertes avancées (6 types)', 'API illimitée', 'Export CSV/JSON',
  'Analytics Copilot (GPT-4)', 'Monte Carlo 50k iter.', 'SimulationLab',
  'Player Intelligence', 'Market Intelligence', 'Event Detection',
  'Webhooks temps réel', 'Custom AI Models', 'Quant Terminal',
  'Données brutes NHL', 'SLA 99.9%', 'Support prioritaire 24h',
];

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1400;
          const steps = 40;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

export default function LandingPageClient() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* ── NAV ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background/95 backdrop-blur border-b border-border shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <AppLogo size={30} />
            <div>
              <span className="text-sm font-bold text-foreground tracking-tight">NHL Analytics Pro</span>
              <span className="ml-2 text-2xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-mono-data border border-primary/20">BETA</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Fonctionnalités</a>
            <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Tarifs</a>
            <a href="#testimonials" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Témoignages</a>
            <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Dashboard</Link>
          </nav>
          <Link
            href="/register"
            className="px-5 py-2 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-all hover:shadow-lg hover:shadow-primary/20 active:scale-95"
          >
            S'inscrire →
          </Link>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-background to-purple-900/10 pointer-events-none" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left — copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-positive/10 border border-positive/25 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />
                <span className="text-xs text-positive font-medium font-mono-data">NHL 2025–26 · DONNÉES LIVE</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold text-foreground leading-[1.08] mb-6 tracking-tight">
                Analysez la NHL<br />
                <span className="text-primary">comme un quant.</span>
              </h1>

              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
                Données live en 30s, terminal Bloomberg-style et 6 agents IA autonomes. La plateforme analytique NHL de référence.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Link
                  href="/register"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all hover:shadow-xl hover:shadow-primary/25 active:scale-95"
                >
                  <Icon name="RocketLaunchIcon" size={16} />
                  Commencer — 1 mois gratuit
                </Link>
                <a
                  href="#features"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-border rounded-xl font-medium text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all"
                >
                  Voir les fonctionnalités
                </a>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="ShieldCheckIcon" size={14} className="text-positive" />
                <span>30 jours gratuits · Puis $29/mois · Annulation à tout moment · Aucune carte requise</span>
              </div>
            </div>

            {/* Right — live stats card */}
            <div className="relative">
              <div className="relative p-6 rounded-2xl bg-card border border-border shadow-2xl shadow-black/20">
                {/* Live badge */}
                <div className="flex items-center justify-between mb-5">
                  <span className="text-xs font-bold text-foreground tracking-wider">TABLEAU DE BORD LIVE</span>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-positive/10 border border-positive/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />
                    <span className="text-2xs text-positive font-mono-data font-medium">LIVE</span>
                  </div>
                </div>

                {/* Metrics grid */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {[
                    { label: 'Uptime données', value: '99.2%', color: 'text-positive', bg: 'bg-positive/8' },
                    { label: 'Délai données', value: '30s', color: 'text-primary', bg: 'bg-primary/8' },
                    { label: 'Analystes actifs', value: '5,000+', color: 'text-foreground', bg: 'bg-muted/30' },
                    { label: 'Projections/jour', value: '12,400', color: 'text-foreground', bg: 'bg-muted/30' },
                  ].map((m) => (
                    <div key={m.label} className={`p-4 rounded-xl ${m.bg} border border-border`}>
                      <div className={`text-xl font-bold font-mono-data ${m.color}`}>{m.value}</div>
                      <div className="text-2xs text-muted-foreground mt-0.5">{m.label}</div>
                    </div>
                  ))}
                </div>

                {/* Mini chart simulation */}
                <div className="p-4 rounded-xl bg-muted/20 border border-border">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-2xs text-muted-foreground font-medium">Activité — 8 derniers mois</span>
                    <span className="text-2xs text-positive font-mono-data">↑ +18%</span>
                  </div>
                  <div className="flex items-end gap-1 h-10">
                    {[62, 65, 68, 70, 71, 73, 74, 75].map((v, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-sm bg-primary/60 transition-all"
                        style={{ height: `${(v / 80) * 100}%`, opacity: 0.4 + i * 0.08 }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 px-4 py-2.5 rounded-xl bg-card border border-positive/30 shadow-lg flex items-center gap-2">
                <Icon name="CheckBadgeIcon" size={16} className="text-positive" />
                <span className="text-xs font-semibold text-foreground">Données validées indépendamment</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="py-10 border-y border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { target: 5000, suffix: '+', label: 'Analystes actifs' },
              { target: 99, suffix: '.2%', label: 'Uptime données' },
              { target: 2100000, suffix: '+', label: 'Projections générées' },
              { target: 6, suffix: '', label: 'Agents IA actifs' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-bold text-primary font-mono-data mb-1">
                  <AnimatedCounter target={s.target} suffix={s.suffix} />
                </div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENTO FEATURES ── */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
              <span className="text-2xs text-primary font-mono-data font-medium">FONCTIONNALITÉS</span>
            </div>
            <h2 className="text-4xl font-bold text-foreground mb-4 tracking-tight">
              Tout l'arsenal d'un quant.<br />
              <span className="text-muted-foreground font-normal">Pour la NHL.</span>
            </h2>
            <p className="text-muted-foreground max-w-xl">
              Une plateforme complète construite pour les analystes sérieux — pas un outil de fantasy basique.
            </p>
          </div>

          {/* Bento grid — asymmetric */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Large card — Live Data */}
            <div className={`md:col-span-2 md:row-span-2 p-8 rounded-2xl bg-gradient-to-br ${FEATURES_BENTO[0].accent} border ${FEATURES_BENTO[0].border} relative overflow-hidden group hover:shadow-xl hover:shadow-positive/10 transition-all duration-300`}>
              <div className="absolute top-0 right-0 w-48 h-48 bg-positive/5 rounded-full blur-2xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-positive/15 flex items-center justify-center">
                    <Icon name="SignalIcon" size={20} className="text-positive" />
                  </div>
                  <span className="text-2xs font-bold tracking-widest text-positive font-mono-data">{FEATURES_BENTO[0].tag}</span>
                </div>
                <div className="text-6xl font-bold text-positive font-mono-data mb-2">{FEATURES_BENTO[0].metric}</div>
                <div className="text-xs text-muted-foreground mb-4">{FEATURES_BENTO[0].metricLabel}</div>
                <h3 className="text-2xl font-bold text-foreground mb-2">{FEATURES_BENTO[0].title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">{FEATURES_BENTO[0].subtitle}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{FEATURES_BENTO[0].desc}</p>
              </div>
            </div>

            {/* Small cards */}
            {FEATURES_BENTO.slice(1, 3).map((f) => (
              <div
                key={f.id}
                className={`p-6 rounded-2xl bg-gradient-to-br ${f.accent} border ${f.border} group hover:shadow-lg transition-all duration-300`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                    <Icon name={f.icon as any} size={16} className="text-foreground" />
                  </div>
                  <span className="text-2xs font-bold tracking-widest text-muted-foreground font-mono-data">{f.tag}</span>
                </div>
                <div className="text-3xl font-bold font-mono-data text-foreground mb-1">{f.metric}</div>
                <div className="text-2xs text-muted-foreground mb-3">{f.metricLabel}</div>
                <h3 className="text-base font-bold text-foreground mb-1">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}

            {/* Medium cards */}
            {FEATURES_BENTO.slice(3).map((f) => (
              <div
                key={f.id}
                className={`p-6 rounded-2xl bg-gradient-to-br ${f.accent} border ${f.border} group hover:shadow-lg transition-all duration-300`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                    <Icon name={f.icon as any} size={16} className="text-foreground" />
                  </div>
                  <span className="text-2xs font-bold tracking-widest text-muted-foreground font-mono-data">{f.tag}</span>
                </div>
                <div className="text-3xl font-bold font-mono-data text-foreground mb-1">{f.metric}</div>
                <div className="text-2xs text-muted-foreground mb-3">{f.metricLabel}</div>
                <h3 className="text-base font-bold text-foreground mb-1">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-24 px-6 bg-card/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left — copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-positive/10 border border-positive/25 mb-6">
                <Icon name="GiftIcon" size={12} className="text-positive" />
                <span className="text-2xs text-positive font-mono-data font-medium">OFFRE DE LANCEMENT</span>
              </div>
              <h2 className="text-4xl font-bold text-foreground mb-4 tracking-tight">
                Un seul tarif.<br />
                <span className="text-primary">Tout inclus.</span>
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Pas de plans Basic/Pro/Enterprise. Pas de fonctionnalités cachées derrière un tier supérieur. Tout ce que la plateforme offre — pour $29/mois.
              </p>

              {/* Features list — 2 cols */}
              <div className="grid grid-cols-2 gap-2">
                {INCLUDED.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="CheckIcon" size={12} className="text-positive flex-shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — pricing card */}
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 bg-primary/10 rounded-3xl blur-2xl scale-95 pointer-events-none" />

              <div className="relative p-8 rounded-2xl bg-card border border-primary/40 shadow-2xl shadow-primary/10">
                {/* Trial badge */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="flex items-center gap-2 px-5 py-2 rounded-full bg-positive text-white text-xs font-bold shadow-lg shadow-positive/30">
                    <Icon name="GiftIcon" size={14} />
                    1 MOIS GRATUIT
                  </div>
                </div>

                <div className="text-center pt-4 mb-8">
                  <div className="text-xs font-bold tracking-widest text-primary mb-4 font-mono-data">NHL ANALYTICS PRO</div>

                  {/* Price display */}
                  <div className="flex items-end justify-center gap-2 mb-2">
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground line-through">$29</div>
                      <div className="text-2xs text-muted-foreground">1er mois</div>
                    </div>
                    <div className="text-7xl font-bold text-foreground leading-none">$0</div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    Puis <span className="text-foreground font-semibold">$29/mois</span> · Toutes fonctionnalités · Annulation à tout moment
                  </p>
                </div>

                {/* Divider */}
                <div className="border-t border-border mb-6" />

                {/* Trust signals */}
                <div className="space-y-3 mb-8">
                  {[
                    { icon: 'ShieldCheckIcon', text: 'Aucune carte de crédit requise pour commencer', color: 'text-positive' },
                    { icon: 'ClockIcon', text: '30 jours d\'accès complet à toutes les fonctionnalités', color: 'text-primary' },
                    { icon: 'XCircleIcon', text: 'Annulation en 1 clic, sans engagement', color: 'text-muted-foreground' },
                  ].map((t) => (
                    <div key={t.text} className="flex items-center gap-3 text-xs text-muted-foreground">
                      <Icon name={t.icon as any} size={14} className={t.color} />
                      <span>{t.text}</span>
                    </div>
                  ))}
                </div>

                <Link
                  href="/register"
                  className="block w-full py-4 rounded-xl text-sm font-bold text-center bg-primary text-white hover:bg-primary/90 transition-all hover:shadow-lg hover:shadow-primary/30 active:scale-[0.98]"
                >
                  Commencer mon essai gratuit →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-foreground mb-3 tracking-tight">Ce que disent les analystes</h2>
            <p className="text-muted-foreground">5,000+ professionnels font confiance à NHL Analytics Pro</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {SOCIAL_PROOF.map((t, i) => (
              <div
                key={t.name}
                className={`p-6 rounded-2xl border transition-all hover:shadow-lg ${
                  i === 1 ? 'bg-primary/5 border-primary/25 md:-translate-y-2' : 'bg-card border-border'
                }`}
              >
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Icon key={j} name="StarIcon" size={14} className="text-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-foreground leading-relaxed mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-xs font-bold text-primary">
                    {t.avatar}
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-foreground">{t.name}</div>
                    <div className="text-2xs text-muted-foreground">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EMAIL CAPTURE / CTA ── */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="relative p-12 rounded-3xl bg-gradient-to-br from-primary/12 via-card to-purple-900/10 border border-primary/25 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Icon name="RocketLaunchIcon" size={12} className="text-primary" />
                <span className="text-2xs text-primary font-mono-data font-medium">REJOINDRE LA LISTE D'ATTENTE</span>
              </div>

              <h2 className="text-4xl font-bold text-foreground mb-4 tracking-tight">
                Prêt à analyser<br />comme un pro ?
              </h2>
              <p className="text-muted-foreground mb-8">
                Accès prioritaire + 30 jours gratuits pour les 500 premiers inscrits.
              </p>

              {submitted ? (
                <div className="flex items-center justify-center gap-3 p-5 rounded-xl bg-positive/10 border border-positive/25">
                  <Icon name="CheckCircleIcon" size={22} className="text-positive" />
                  <div className="text-left">
                    <div className="text-sm font-semibold text-foreground">Vous êtes sur la liste !</div>
                    <div className="text-xs text-muted-foreground">Nous vous contacterons en priorité pour l'accès.</div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    required
                    className="flex-1 px-4 py-3 rounded-xl bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all hover:shadow-lg hover:shadow-primary/25 active:scale-95 whitespace-nowrap"
                  >
                    Rejoindre →
                  </button>
                </form>
              )}

              <p className="text-2xs text-muted-foreground mt-4">
                Ou <Link href="/register" className="text-primary hover:underline">créer un compte directement — 1 mois gratuit</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-border py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <AppLogo size={22} />
            <div>
              <span className="text-sm font-semibold text-foreground">NHL Analytics Pro</span>
              <span className="text-2xs text-muted-foreground ml-2">© 2025</span>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {[
              { label: 'Dashboard', href: '/' },
              { label: 'Fonctionnalités', href: '#features' },
              { label: 'Tarifs', href: '#pricing' },
              { label: 'Transparence', href: '/trust-dashboard' },
              { label: 'Inscription', href: '/register' },
              { label: 'Launch Page', href: '/launch' },
            ].map((l) => (
              <Link key={l.label} href={l.href} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />
            <span>Tous systèmes opérationnels</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
